import React, { useEffect, useRef } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { WebService } from "../../Services/WebService";
import { Form, FormInputText, FormInputDropdown } from "../Form";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { WSErrorAlert, WSSuccessAlert, WSWarningAlert } from "../../Services/WSAlert";
import { StandardConst } from "../../Services/StandardConst";
import { DateTime } from "luxon";
import { addDays } from "date-fns";

const { forwardRef, useState, useImperativeHandle } = React;

const AddEditAttendanceRegularization = (prop, ref) => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [data, setData] = useState({ RegularizationRequest: {} });
  const [empId, setEmployeeId] = useState(null);
  const [empData, setEmpData] = useState(null);
  const [empManager, setManagerId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const Auth = useSelector((s) => s.auth ?? {});
  // console.log("Auth:", Auth);

  const RequestTypes = {
    'Missed Punch': 1,
    'Incorrect Punch': 2,
    'On Duty': 3,
    'Work from Home': 4,
    'Other': 5
  };
  const styleObj = {
    fontSize: 14,
    fontFamily: "sans-serif",
    color: "rgb(108 106 106)",
    paddingTop: "6px",
  };
  // Set empId from Auth when component mounts or Auth changes
  useEffect(() => {
    if (Auth?.BasicInfo?.EmployeeId) {
      // console.log("✅ Setting Employee ID from Auth:", Auth.BasicInfo.EmployeeId);
      setEmployeeId(Auth.BasicInfo.EmployeeId);
    }
  }, [Auth]);

  const fetchEmpData = async () => {
    try {
      // Fetch all managers
      const managers = await WebService({
        endPoint: `UserProfile/FetchManager`,
        dispatch,
      });

      // console.log("Managers:", managers);

      // Fetch employee data
      const empData = await WebService({
        endPoint: `UserProfile/Fetch/${Auth?.BasicInfo?.EmployeeId || 0}`,
        dispatch,
      });

      // console.log("Employee Data:", empData);
      setEmpData(empData);

      // Fetch company details if CompanyId exists
      if (empData[0]?.CompanyId > 0) {
        const CompanyData = await WebService({
          endPoint: `CompanyProfileList/fetch/${empData[0]?.CompanyId}`,
          dispatch,
        });
        // console.log("Company Data:", CompanyData);
      }

      // Find manager id using EmployeeManager field
      const employeeManagerName = empData[0]?.EmployeeManager;
      const managerRecord = managers.find(
        (m) => m.FullName.toLowerCase() === employeeManagerName?.toLowerCase()
      );

      if (managerRecord) {
        // console.log("Manager Found:", managerRecord.EmployeeId, managerRecord.FullName);
        setManagerId(managerRecord.EmployeeId);
      } else {
        console.log("Manager not found in manager list");
      }
    } catch (error) {
      console.error("❌ Error fetching Emp Data:", error);
    }
  };

  useEffect(() => {
    if (Auth?.BasicInfo?.EmployeeId) {
      // console.log("🔄 Auth Component mounted, fetching data...", Auth.BasicInfo.EmployeeId);
      fetchEmpData();
    }
  }, [Auth]);

  useImperativeHandle(ref, () => ({
    openModal: async (id) => {
      try {
        // console.log("✏️ AddEdit - Opening modal for ID:", id);
        // console.log("👤 Current empId state:", empId);
        // console.log("👤 Auth EmployeeId:", Auth?.BasicInfo?.EmployeeId);

        // Set empId immediately if not set
        const currentEmpId = empId || Auth?.BasicInfo?.EmployeeId;
        if (currentEmpId && !empId) {
          setEmployeeId(currentEmpId);
        }

        if (id === StandardConst.YesNo.No || id === 0) {
          // New request
          const todayDate = new Date().toISOString();
          const data = await WebService({
            endPoint: `AttendanceRegularization/Fetch/0`,
            dispatch,
          });

          data.RegularizationRequest = data.RegularizationRequest || {};
          data.RegularizationRequest.RequestDate = DateTime.fromISO(todayDate).toSQLDate();
          data.RegularizationRequest.AttendanceDate = DateTime.fromISO(todayDate).toSQLDate();
          data.RegularizationRequest.RequestType = RequestTypes["Missed Punch"];

          data.requestTypes = Object.keys(RequestTypes).map((key) => ({
            value: RequestTypes[key],
            text: key,
          }));

          console.log("✅ AddEdit - New request data loaded");
          console.log("👤 EmployeeData:", data?.EmployeeData);

          setData(data);

          // Set empId from multiple sources
          const empIdToSet = data?.EmployeeData?.[0]?.EmployeeId || currentEmpId;
          console.log("👤 Setting empId to:", empIdToSet);
          setEmployeeId(empIdToSet);

          setSelectedFile(null);
          setShow(true);
        } else {
          // Edit existing request
          const data = await WebService({
            endPoint: `AttendanceRegularization/Fetch/${id}`,
            dispatch,
          });

          data.requestTypes = Object.keys(RequestTypes).map((key) => ({
            value: RequestTypes[key],
            text: key,
          }));

          if (data.RegularizationRequest?.CorrectedInTime) {
            data.RegularizationRequest.CorrectedInTime = DateTime.fromSQL(
              data.RegularizationRequest.CorrectedInTime
            ).toFormat("HH:mm");
          }
          if (data.RegularizationRequest?.CorrectedOutTime) {
            data.RegularizationRequest.CorrectedOutTime = DateTime.fromSQL(
              data.RegularizationRequest.CorrectedOutTime
            ).toFormat("HH:mm");
          }

          console.log("✅ AddEdit - Edit data loaded");
          setData(data);

          const empIdToSet = data?.EmployeeData?.[0]?.EmployeeId || currentEmpId;
          console.log("👤 Setting empId to:", empIdToSet);
          setEmployeeId(empIdToSet);

          setSelectedFile(null);
          setShow(true);
        }
      } catch (error) {
        console.error("❌ AddEdit - Error opening modal:", error);
        alert("Error loading regularization form: " + error.message);
      }
    },
  }));

  const handleClose = () => {
    setShow(false);
    setData({ RegularizationRequest: {} });
    setSelectedFile(null);
  };

  const schema = yup
    .object()
    .shape({
      AttendanceDate: yup.string().trim().required(StandardConst.requiredMessage),
      RequestType: yup.number().required(StandardConst.requiredMessage),
      Remarks: yup.string().trim().required(StandardConst.requiredMessage),
      CorrectedInTime: yup.string().required(StandardConst.requiredMessage),
      CorrectedOutTime: yup.string().required(StandardConst.requiredMessage),
    })
    .required();

  const formatDate = (dateString, format = "DD-MM-YYYY") => {
    if (!dateString) return "";
    const d = new Date(dateString);
    const iso = d.toISOString().split("T")[0]; // YYYY-MM-DD
    if (format === "YYYY-MM-DD") return iso;
    const [y, m, d2] = iso.split("-");
    return `${d2}-${m}-${y}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return DateTime.fromSQL(timeString).toFormat("hh:mm a");
  };

const onSubmit = async (formData) => {
  try {
    console.log("📤 ========== SUBMIT STARTED ==========");
    
    const employeeIdToSubmit = empId || Auth?.BasicInfo?.EmployeeId || formData.EmployeeId;

    if (!employeeIdToSubmit) {
      WSErrorAlert("Error", "Employee ID is missing. Please refresh and try again.");
      return;
    }

    // console.log("✅ Using Employee ID:", employeeIdToSubmit);

    // Check existing requests
    try {
      const existingRequests = await WebService({
        endPoint: `AttendanceRegularization/CheckExisting?attendanceDate=${formData.AttendanceDate}`,
        method: "GET",
        dispatch,
      });

      console.log('checking if existing date->',formData.AttendanceDate);
      console.log('exsting record responce',existingRequests);

      if (existingRequests.length > 0 && !formData.RegularizationId) {
        WSWarningAlert("", "A regularization request already exists for this date.");
        return;
      }
    } catch (err) {
      console.log("⚠️ Could not check existing requests:", err);
    }

    // ✅ Step 1: Upload file first (if selected) using your existing upload endpoint
    let attachmentFileName = null;
    
    if (selectedFile) {
      console.log("📤 Uploading file first...");
      
      try {
        const uploadResult = await WebService({
          endPoint: "upload/File",
          body: selectedFile,
          dispatch,
          isFile: true,
        });
        
        attachmentFileName = uploadResult.filename;
        console.log("✅ File uploaded successfully:", attachmentFileName);
      } catch (uploadError) {
        console.error("❌ File upload failed:", uploadError);
        WSErrorAlert("Error", "Failed to upload attachment. Please try again.");
        return;
      }
    }

    // ✅ Step 2: Submit the form data with filename (as JSON)
    const submitData = {
      RegularizationId: formData.RegularizationId || undefined,
      EmployeeId: employeeIdToSubmit,
      RequestDate: formData.RequestDate,
      AttendanceDate: formData.AttendanceDate,
      RequestType: formData.RequestType,
      CorrectedInTime: formData.CorrectedInTime || null,
      CorrectedOutTime: formData.CorrectedOutTime || null,
      Remarks: formData.Remarks,
      StatusId: StandardConst.RegulrizationStatus.Pending,
      Attachment: attachmentFileName, // ✅ Send filename, not file
    };

    console.log("📤 Submitting data:", submitData);

    await WebService({
      endPoint: "AttendanceRegularization/Submit",
      body: submitData,
      dispatch,
    });

    console.log("✅ Submit successful!");

    WSSuccessAlert(
      "Success",
      `Attendance Regularization for ${DateTime.fromISO(formData.AttendanceDate).toFormat("dd-MM-yyyy")} submitted successfully.`
    );

    handleClose();

    if (prop.callBackEvent) {
      prop.callBackEvent();
    }

    // Send Notification
    try {
      if (empManager > 0) {
        console.log('Sending notification to manager:', empManager);
        const formatted = new Date(formData.AttendanceDate).toISOString().split("T")[0];

        await WebService({
          dispatch,
          endPoint: "Notification/Create",
          body: {
            Message: `${Auth?.BasicInfo?.FullName || "Employee"} has submitted a regularization request for ${formatted}`,
            SendTo: [empManager],
            Expire: addDays(new Date(), 1),
          },
        });
      }
    } catch (err) {
      console.log("⚠️ Notification failed:", err);
    }

    // Send Email
    try {
      const RequestTypesReverse = Object.fromEntries(
        Object.entries(RequestTypes).map(([key, value]) => [value, key])
      );

      const newData = {
        ...formData,
        EmployeeName: Auth?.BasicInfo?.FullName || "",
        StatusId: "Pending",
        RequestTypeText: RequestTypesReverse[formData.RequestType] || "Unknown",
        FormattedAttendanceDate: formatDate(formData.AttendanceDate),
        FormattedRequestDate: formatDate(formData.RequestDate),
        FormattedInTime: formatTime(formData.CorrectedInTime),
        FormattedOutTime: formatTime(formData.CorrectedOutTime),
      };

      await WebService({
        endPoint: "Email/send-regularization-mail",
        body: { data: newData, employeeID: employeeIdToSubmit },
        dispatch,
      });
    } catch (err) {
      console.log("⚠️ Email failed:", err);
    }

  } catch (error) {
    console.error("❌ Error submitting regularization:", error);
    console.error("Error stack:", error.stack);
    WSErrorAlert("Error", "Failed to submit: " + (error.message || "Unknown error"));
  }
};

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log("📁 File selected:", file.name, file.size, "bytes");
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };
console.log(data,'Saved data fetch');
  return (
    <Modal
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      show={show}
      onHide={handleClose}
      centered
      className="container-fluid"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {(data.RegularizationRequest.RegularizationId || 0) === 0
            ? "Raise Attendance Regularization"
            : data.RegularizationRequest.StatusId === StandardConst.RegulrizationStatus.Approved
            ? "Request Already Approved. You Can't Edit"
            : "Edit Regularization Request"}
        </Modal.Title>
      </Modal.Header>

      <Form
        defaultValues={{
          ...data.RegularizationRequest,
          Attachment: undefined,
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
        ref={formRef}
      >
        <Modal.Body className="p-4">
          <div>
            <FormInputText
              name="RequestDate"
              label="Request Date"
              type="date"
              disabled
            />
          </div>

          <div className="mt-2">
            <FormInputText
              name="AttendanceDate"
              label="Attendance Date"
              type="date"
              setValue={(val) =>
                setData((data) => ({
                  ...data,
                  RegularizationRequest: {
                    ...data.RegularizationRequest,
                    AttendanceDate: val,
                  },
                }))
              }
              max={new Date().toISOString().split("T")[0]}
              isRequired="true"
              disabled={data.RegularizationRequest.StatusId !== undefined}
            />
          </div>

          <div className="mt-2">
            <FormInputDropdown
              name="RequestType"
              label="Type of Request"
              ddOpt={data.requestTypes}
              setValue={(val) => {
                setData((data) => ({
                  ...data,
                  RegularizationRequest: {
                    ...data.RegularizationRequest,
                    RequestType: parseInt(val),
                  },
                }));
              }}
              isRequired="true"
              disabled={
                data.RegularizationRequest.StatusId !== StandardConst.RegulrizationStatus.Pending &&
                data.RegularizationRequest.StatusId !== undefined
              }
            />
          </div>

          <div className="mt-2">
            <FormInputText
              name="CorrectedInTime"
              label="Corrected In Time"
              type="time"
              isRequired="true"
              disabled={
                data.RegularizationRequest.StatusId !== StandardConst.RegulrizationStatus.Pending &&
                data.RegularizationRequest.StatusId !== undefined
              }
            />
          </div>

          <div className="mt-2">
            <FormInputText
              name="CorrectedOutTime"
              label="Corrected Out Time"
              type="time"
              isRequired="true"
              disabled={
                data.RegularizationRequest.StatusId !== StandardConst.RegulrizationStatus.Pending &&
                data.RegularizationRequest.StatusId !== undefined
              }
            />
          </div>

          <div className="mt-2">
            <FormInputText
              name="Remarks"
              label="Reason for Regularization"
              as="textarea"
              rows={4}
              isRequired="true"
              disabled={
                data.RegularizationRequest.StatusId !== StandardConst.RegulrizationStatus.Pending &&
                data.RegularizationRequest.StatusId !== undefined
              }
            />
          </div>

           {data?.RegularizationRequest?.Attachment && (
                       <div className="row mt-2">
                         <div className="col-md-12">
                           <label style={styleObj}>Attachment</label>
                           {/* <img class="pic rounded-circle" src={`${StandardConst.apiBaseUrl}/uploads/${data.Attachment}` ||  `${StandardConst.apiBaseUrl}/uploads/user_placeholder.jpg`} alt=""></img> */}
                           <br />
                           <img src={`${StandardConst.apiBaseUrl}/uploads/${data?.RegularizationRequest?.Attachment}` || `${StandardConst.apiBaseUrl}/uploads/user_placeholder.jpg`} alt="uploads" width="100px"/>
                         </div>
                       </div>
                     )}
          <div className="mt-2">
            <label className="form-label">Attachment (Optional)</label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              disabled={
                data.RegularizationRequest.StatusId !== StandardConst.RegulrizationStatus.Pending &&
                data.RegularizationRequest.StatusId !== undefined
              }
            />
            {selectedFile && (
              <small className="text-success d-block mt-1">
                ✓ {selectedFile.name} selected ({(selectedFile.size / 1024).toFixed(2)} KB)
              </small>
            )}
          </div>

          {/* Hidden field for EmployeeId */}
          {empId && <input type="hidden" name="EmployeeId" value={empId} />}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-primary"
            id="btnSubmitRegularization"
            type="submit"
            disabled={
              data.RegularizationRequest.StatusId === StandardConst.RegulrizationStatus.Approved ||
              data.RegularizationRequest.StatusId === StandardConst.RegulrizationStatus.Rejected
            }
          >
            {(data.RegularizationRequest.RegularizationId || 0) === 0
              ? "Submit Request"
              : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default forwardRef(AddEditAttendanceRegularization);